import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, FileText, Receipt, Banknote, ChevronRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';

const ALLOWED = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'];

export default function SupplierDashboard() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [stmtBuilding, setStmtBuilding] = useState('all');

  useEffect(() => {
    if (!ALLOWED.includes(user?.role)) return;
    (async () => {
      setLoading(true);
      try {
        const [s, p, b] = await Promise.all([
          axios.get(`${API}/supplier-dashboard/summary`, { withCredentials: true }),
          axios.get(`${API}/supplier-dashboard/per-supplier`, { withCredentials: true }),
          axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setSummary(s.data);
        setRows(p.data || []);
        setBuildings(b.data || []);
      } catch { toast.error('Failed to load supplier dashboard'); }
      setLoading(false);
    })();
  }, [user]);

  const openStatement = async (supplierId, building = 'all') => {
    try {
      const url = building && building !== 'all'
        ? `${API}/supplier-dashboard/statement/${supplierId}?building_id=${building}`
        : `${API}/supplier-dashboard/statement/${supplierId}`;
      const r = await axios.get(url, { withCredentials: true });
      setStatement({ ...r.data, supplier: rows.find(x => x.supplier_id === supplierId) });
      setStmtBuilding(building);
    } catch { toast.error('Failed to load statement'); }
  };

  if (!ALLOWED.includes(user?.role)) {
    return <Layout><div className="p-6 text-center text-muted-foreground">Forbidden — only managers and admins can view this page.</div></Layout>;
  }

  return (
    <Layout>
      <div data-testid="supplier-dashboard-page" className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-amber-600" />Supplier Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">Pending POs, pending invoices, and statements of account per supplier.</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Pending POs</div>
            <div data-testid="dash-pending-po-count" className="text-2xl font-bold text-foreground">{summary?.pending_po_count || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">{formatCurrency(summary?.pending_po_value || 0, symbol)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" />Pending Bills</div>
            <div data-testid="dash-pending-bill-count" className="text-2xl font-bold text-foreground">{summary?.pending_bill_count || 0}</div>
            <div className="text-xs text-muted-foreground mt-1">{formatCurrency(summary?.pending_bill_outstanding || 0, symbol)}</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-amber-700 flex items-center gap-1"><Banknote className="w-3.5 h-3.5" />Outstanding</div>
            <div className="text-2xl font-bold text-amber-700">{formatCurrency((summary?.pending_bill_outstanding || 0) + (summary?.pending_po_value || 0), symbol)}</div>
            <div className="text-xs text-muted-foreground mt-1">PO commitments + unpaid bills</div>
          </Card>
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500">Suppliers</div>
            <div className="text-2xl font-bold text-foreground">{rows.length}</div>
            <div className="text-xs text-muted-foreground mt-1">Active in dashboard</div>
          </Card>
        </div>

        {/* Per-supplier table */}
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3 text-right">Pending POs</th>
                <th className="px-4 py-3 text-right">PO Value</th>
                <th className="px-4 py-3 text-right">Delivered POs</th>
                <th className="px-4 py-3 text-right">Pending Bills</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3 text-right">Paid YTD</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="8" className="text-center py-12 text-slate-500">Loading…</td></tr> :
              rows.length === 0 ? <tr><td colSpan="8" className="text-center py-12 text-slate-500">No supplier activity yet</td></tr> :
              rows.map(r => (
                <tr key={r.supplier_id} data-testid={`supplier-row-${r.supplier_id}`} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-foreground">{r.supplier_name}</div>
                    {r.service_type && <div className="text-xs text-muted-foreground capitalize">{r.service_type}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">{r.pending_po_count}</td>
                  <td className="px-4 py-3 text-right text-amber-700 font-medium">{formatCurrency(r.pending_po_value, symbol)}</td>
                  <td className="px-4 py-3 text-right">{r.delivered_po_count}</td>
                  <td className="px-4 py-3 text-right">{r.pending_bill_count}</td>
                  <td className="px-4 py-3 text-right text-rose-700 font-medium">{formatCurrency(r.pending_bill_outstanding, symbol)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-medium">{formatCurrency(r.paid_total, symbol)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" data-testid={`view-statement-${r.supplier_id}`} onClick={() => openStatement(r.supplier_id)}>
                      View<ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* Statement of account dialog */}
        {statement && (
          <Dialog open onOpenChange={() => setStatement(null)}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Statement of Account — {statement.supplier?.supplier_name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Filter by building:</span>
                  <Select value={stmtBuilding} onValueChange={(v) => openStatement(statement.supplier_id, v)}>
                    <SelectTrigger data-testid="stmt-building-select" className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All buildings</SelectItem>
                      {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <Card className="p-3"><div className="text-xs text-muted-foreground">Total Billed</div><div className="text-lg font-bold">{formatCurrency(statement.summary.total_billed, symbol)}</div></Card>
                  <Card className="p-3"><div className="text-xs text-muted-foreground">Total Paid</div><div className="text-lg font-bold text-emerald-700">{formatCurrency(statement.summary.total_paid, symbol)}</div></Card>
                  <Card className="p-3"><div className="text-xs text-muted-foreground">Balance Due</div><div className="text-lg font-bold text-rose-700">{formatCurrency(statement.summary.balance_due, symbol)}</div></Card>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2 text-sm">Purchase Orders ({statement.purchase_orders.length})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left border-b text-slate-500"><th className="py-2">PO #</th><th className="py-2">Issue date</th><th className="py-2">Status</th><th className="py-2 text-right">Total</th></tr></thead>
                      <tbody>
                        {statement.purchase_orders.map(po => (
                          <tr key={po.id} className="border-b last:border-0">
                            <td className="py-1.5 font-mono">{po.po_number}</td>
                            <td className="py-1.5">{po.issue_date}</td>
                            <td className="py-1.5 capitalize">{po.status}</td>
                            <td className="py-1.5 text-right">{formatCurrency(po.total_amount, symbol)}</td>
                          </tr>
                        ))}
                        {statement.purchase_orders.length === 0 && <tr><td colSpan="4" className="text-center py-3 text-slate-400">None</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2 text-sm">Bills ({statement.bills.length})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left border-b text-slate-500"><th className="py-2">Bill #</th><th className="py-2">Issue</th><th className="py-2">Status</th><th className="py-2 text-right">Total</th><th className="py-2 text-right">Paid</th><th className="py-2">Attachment</th></tr></thead>
                      <tbody>
                        {statement.bills.map(b => (
                          <tr key={b.id} className="border-b last:border-0">
                            <td className="py-1.5 font-mono">{b.bill_number}</td>
                            <td className="py-1.5">{b.issue_date}</td>
                            <td className="py-1.5 capitalize">{b.status}</td>
                            <td className="py-1.5 text-right">{formatCurrency(b.total_amount || b.amount, symbol)}</td>
                            <td className="py-1.5 text-right">{formatCurrency(b.paid_amount || 0, symbol)}</td>
                            <td className="py-1.5">{b.attachment_url ? <a href={b.attachment_url} target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" />View</a> : '—'}</td>
                          </tr>
                        ))}
                        {statement.bills.length === 0 && <tr><td colSpan="6" className="text-center py-3 text-slate-400">None</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2 text-sm">Payments ({statement.payments.length})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left border-b text-slate-500"><th className="py-2">Date</th><th className="py-2">Method</th><th className="py-2">Reference</th><th className="py-2 text-right">Amount</th></tr></thead>
                      <tbody>
                        {statement.payments.map(p => (
                          <tr key={p.id} className="border-b last:border-0">
                            <td className="py-1.5">{p.paid_at}</td>
                            <td className="py-1.5 capitalize">{p.method}</td>
                            <td className="py-1.5">{p.reference || '—'}</td>
                            <td className="py-1.5 text-right text-emerald-700 font-medium">{formatCurrency(p.amount, symbol)}</td>
                          </tr>
                        ))}
                        {statement.payments.length === 0 && <tr><td colSpan="4" className="text-center py-3 text-slate-400">None</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
